import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { friendlyError } from '../utils/friendlyError';
import { useGoogleSignInAvailable, useGoogleButtonWidth } from '../utils/googleAuth';
import './Register.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import { AuthShell } from './auth/AuthShell';
import { Input, Label } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { StatePanel, ErrorCard } from '../components/ui/StatePanel';
import { Rise, SPRING_FAST } from '../components/ui/motion';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { companyAPI } from '../services/api';
import { getPasswordChecks, isPasswordValid } from '../utils/passwordRules';
import { PasswordInput } from '../components/ui/PasswordInput';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    companyName: '',
    department: '',
    position: '',
    role: 'employee' as 'employee' | 'manager' | 'admin',
    isAdmin: false,
  });
  const googleAvailable = useGoogleSignInAvailable();
  const [googleBtnRef, googleBtnWidth] = useGoogleButtonWidth();
  const [error, setError] = useState('');
  /* The Terms box relied on the native `required` attribute alone. Whether the
     browser's validation bubble actually appears depends on the control being
     focusable and on screen, and here it wasn't — pressing "Sign Up" produced no
     error, no highlight and no submission, which reads as a dead button. Track
     it in state and say so. */
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsTouched, setTermsTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleCredential, setGoogleCredential] = useState<string | null>(null);
  const [invitedCompany, setInvitedCompany] = useState<{ companyId: string; name: string } | null>(
    null,
  );
  // Set when ?lanuraInvite= resolves to an admin-type invite: the holder
  // registers as an admin of this (read-only) company.
  const [adminInvite, setAdminInvite] = useState<{ companyName: string | null } | null>(null);
  // Set when ?lanuraInvite= resolves to a trial-type referral invite: the
  // holder founds a brand-new workspace and is promoted to owner. Backend
  // requires companyName, so we render an editable Company input.
  const [trialInvite, setTrialInvite] = useState<{ trialDays: number | null } | null>(null);
  // Set when ?lanuraInvite= resolves to an owner-referral invite: the holder
  // founds a brand-new workspace and is promoted to owner (same as trial) but
  // gets NO free trial — they subscribe directly. Backend requires companyName.
  const [ownerReferralInvite, setOwnerReferralInvite] = useState<boolean>(false);
  // Set when ?lanuraInvite= resolves to an email invite to a team (QA-06).
  // The invite fixes the company, team, role and address; the server
  // refuses any other address, so the email field is filled and locked.
  const [memberInvite, setMemberInvite] = useState<{
    companyName: string | null;
    teamName: string | null;
    role: 'employee' | 'manager';
    email: string | null;
  } | null>(null);

  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const inviteCompanyId = useMemo(
    () => (searchParams.get('companyId') || '').trim(),
    [searchParams],
  );
  const lanuraInvite = useMemo(
    () => (searchParams.get('lanuraInvite') || '').trim(),
    [searchParams],
  );
  /* A referrer's link. Carried through registration so the credit can be
     attributed on this workspace's first payment. */
  const referralCode = useMemo(() => (searchParams.get('ref') || '').trim(), [searchParams]);
  /* The team half of a team invite link, forwarded here by /join when the
     person has no account yet. It only ever narrows: the server checks it
     against the company this registration resolves to and drops it if it
     names a team anywhere else. */
  const inviteTeamId = useMemo(() => (searchParams.get('teamId') || '').trim(), [searchParams]);

  useEffect(() => {
    // Check if redirected from login with Google credential
    if (location.state?.googleCredential) {
      setGoogleCredential(location.state.googleCredential);
    }
  }, [location]);

  useEffect(() => {
    let mounted = true;
    if (!inviteCompanyId) {
      setInvitedCompany(null);
      return;
    }
    (async () => {
      try {
        const resp = await companyAPI.getCompany(inviteCompanyId);
        if (!mounted) return;
        setInvitedCompany({ companyId: resp.company.companyId, name: resp.company.name });
      } catch {
        if (!mounted) return;
        setInvitedCompany(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [inviteCompanyId]);

  useEffect(() => {
    let mounted = true;
    if (!lanuraInvite) {
      setMemberInvite(null);
      setAdminInvite(null);
      setTrialInvite(null);
      setOwnerReferralInvite(false);
      return;
    }
    (async () => {
      try {
        const resp = await companyAPI.lookupInvite(lanuraInvite);
        if (!mounted) return;
        setMemberInvite(null);
        if (resp.valid && resp.type === 'member') {
          setAdminInvite(null);
          setTrialInvite(null);
          setOwnerReferralInvite(false);
          setMemberInvite({
            companyName: resp.companyName ?? null,
            teamName: resp.teamName ?? null,
            role: resp.role === 'manager' ? 'manager' : 'employee',
            email: resp.email ?? null,
          });
          setFormData((f) => ({ ...f, isAdmin: false, email: resp.email || f.email }));
        } else if (resp.valid && resp.type === 'admin') {
          setAdminInvite({ companyName: resp.companyName });
          setTrialInvite(null);
          setOwnerReferralInvite(false);
          // The invite dictates admin; reflect it in the form + payload.
          setFormData((f) => ({ ...f, isAdmin: true, role: 'admin' }));
        } else if (resp.valid && resp.type === 'trial') {
          setAdminInvite(null);
          setTrialInvite({ trialDays: resp.trialDays ?? null });
          setOwnerReferralInvite(false);
          // Trial referral founds a new workspace; not an admin-of-existing flow.
          setFormData((f) => ({ ...f, isAdmin: false, role: 'employee' }));
        } else if (resp.valid && resp.type === 'owner_referral') {
          setAdminInvite(null);
          setTrialInvite(null);
          setOwnerReferralInvite(true);
          // Owner-referral founds a new workspace (owner), no trial.
          setFormData((f) => ({ ...f, isAdmin: false, role: 'employee' }));
        } else {
          setAdminInvite(null);
          setTrialInvite(null);
          setOwnerReferralInvite(false);
        }
      } catch {
        // Invalid/used/lookup failure — backend still validates on submit.
        if (mounted) {
          setAdminInvite(null);
          setTrialInvite(null);
          setOwnerReferralInvite(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [lanuraInvite]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!termsAccepted) {
      setTermsTouched(true);
      setError('Please accept the Terms of Service to continue.');
      return;
    }
    setLoading(true);
    setError('');

    // Check if this is a Google OAuth signup
    if (googleCredential) {
      try {
        await googleLogin(
          googleCredential,
          invitedCompany?.name || formData.company,
          formData.department,
          formData.position,
          {
            companyId: invitedCompany?.companyId || undefined,
            teamId: inviteTeamId || undefined,
            isAdmin: invitedCompany ? false : !!formData.isAdmin,
            companyName: invitedCompany ? undefined : formData.companyName || undefined,
            role: invitedCompany ? 'employee' : formData.isAdmin ? 'admin' : 'employee',
            lanuraInvite: lanuraInvite || undefined,
          },
        );
        navigate('/baseline-quiz');
      } catch (err: any) {
        setError(friendlyError(err, 'Registration failed'));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Regular registration
    if (!isPasswordValid(formData.password)) {
      setError('Please meet all password requirements.');
      setLoading(false);
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        company: invitedCompany?.name || formData.company || formData.companyName || '',
        companyId: invitedCompany?.companyId || undefined,
        teamId: inviteTeamId || undefined,
        companyName: invitedCompany ? undefined : formData.companyName || undefined,
        isAdmin: invitedCompany ? false : !!formData.isAdmin,
        department: formData.department,
        position: formData.position,
        role: invitedCompany
          ? 'employee'
          : formData.isAdmin
            ? 'admin'
            : formData.role || 'employee',
        lanuraInvite: lanuraInvite || undefined,
        referralCode: referralCode || undefined,
      });
      // Registration now requires email verification
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (err: any) {
      setError(friendlyError(err, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]:
        e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value,
    }));
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('Google sign-up failed. Please try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Try to login first (in case user already exists)
      await googleLogin(
        credentialResponse.credential,
        invitedCompany?.name || undefined,
        undefined,
        undefined,
        {
          companyId: invitedCompany?.companyId || undefined,
          teamId: inviteTeamId || undefined,
          isAdmin: invitedCompany ? false : !!formData.isAdmin,
          companyName: invitedCompany ? undefined : formData.companyName || undefined,
          role: invitedCompany ? 'employee' : formData.isAdmin ? 'admin' : 'employee',
          lanuraInvite: lanuraInvite || undefined,
        },
      );
      navigate('/');
    } catch (err: any) {
      if (err.response?.data?.requiresCompany) {
        // New user - store credential and show company form
        setGoogleCredential(credentialResponse.credential);
        setError('Please provide your company information to complete registration.');
      } else {
        setError(friendlyError(err, 'Google sign-up failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-up failed. Please try again.');
  };

  return (
    <AuthShell
      title="Create an account"
      subtitle="Start practising in minutes - you can join a company now or later."
      className="register-page"
    >
      {!googleCredential && googleAvailable && (
        <Rise as="div" index={2}>
          {/* Measured first, rendered second — see useGoogleButtonWidth. */}
          <div className="google-login-wrapper" ref={googleBtnRef}>
            {googleBtnWidth && (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                width={googleBtnWidth}
                text="signup_with"
              />
            )}
          </div>

          <div className="divider">
            <span>or continue with email</span>
          </div>
        </Rise>
      )}

      {googleCredential && (
        <StatePanel
          layout="inline"
          className="info-message"
          description="Complete your profile with company information"
          role="status"
        />
      )}

      <form onSubmit={handleSubmit} className="register-form">
        {error && <ErrorCard layout="inline" description={error} className="error-message" />}

        {!googleCredential && (
          <>
            <Rise as="div" index={3} className="form-group">
              <Label htmlFor="register-name">Name</Label>
              <Input
                id="register-name"
                className="form-input"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your full name"
                required
              />
            </Rise>

            <Rise as="div" index={4} className="form-group">
              <Label htmlFor="register-email">Email</Label>
              <Input
                id="register-email"
                className="form-input"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@gmail.com"
                required
                readOnly={!!memberInvite?.email}
              />
              {memberInvite?.email && (
                <div className="form-help">This invite was sent to this address.</div>
              )}
            </Rise>

            <Rise as="div" index={5} className="form-group">
              <Label htmlFor="register-password">Password</Label>
              <PasswordInput
                id="register-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                minLength={8}
                required
              />

              {/* The checklist grows in on SPRING_FAST rather than popping
                    the form height. */}
              <AnimatePresence initial={false}>
                {formData.password.length > 0 && (
                  <motion.div
                    key="pw-reqs"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={SPRING_FAST}
                    style={{ overflow: 'hidden' }}
                  >
                    <ul className="pw-requirements" aria-label="Password requirements">
                      {getPasswordChecks(formData.password).map((c) => (
                        <li key={c.label} className={c.met ? 'met' : ''}>
                          <span className="pw-req-icon" aria-hidden="true">
                            {c.met ? (
                              <Check className="lnr-icon" />
                            ) : (
                              <span className="pw-req-dot" />
                            )}
                          </span>
                          {c.label}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </Rise>

            <Rise as="div" index={6} className="form-group">
              <Label htmlFor="register-confirmPassword">Confirm password</Label>
              <PasswordInput
                id="register-confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Repeat password"
                required
              />
            </Rise>
          </>
        )}

        {invitedCompany ? (
          <StatePanel
            layout="inline"
            className="info-message"
            role="status"
            description={
              <>
                You’re joining <strong>{invitedCompany.name}</strong>.
              </>
            }
          />
        ) : (
          <>
            {lanuraInvite && (
              <StatePanel
                layout="inline"
                className="register-invite"
                role="status"
                description={
                  memberInvite ? (
                    <>
                      <strong>
                        You've been invited to join{' '}
                        {memberInvite.teamName ? <>{memberInvite.teamName} at </> : null}
                        {memberInvite.companyName || 'a Lanura workspace'} as{' '}
                        {memberInvite.role === 'manager' ? 'a manager' : 'an employee'}.
                      </strong>{' '}
                      Create your account to accept.
                    </>
                  ) : adminInvite ? (
                    <>
                      <strong>
                        🎉 You've been invited as an admin
                        {adminInvite.companyName ? <> of {adminInvite.companyName}</> : null}!
                      </strong>{' '}
                      Create your account to get started.
                    </>
                  ) : trialInvite ? (
                    <>
                      <strong>
                        🎉 You've been invited to try Lanura
                        {trialInvite.trialDays ? (
                          <> - {trialInvite.trialDays}-day free trial</>
                        ) : null}
                        !
                      </strong>{' '}
                      Name your workspace below to get started.
                    </>
                  ) : ownerReferralInvite ? (
                    <>
                      <strong>🎉 You've been referred to Lanura!</strong> Name your workspace below
                      to set up your own account.
                    </>
                  ) : (
                    <>
                      <strong>🎉 You've been invited by Lanura!</strong> Create an account to get
                      started.
                    </>
                  )
                }
              />
            )}

            {lanuraInvite && !trialInvite && !ownerReferralInvite && !memberInvite && (
              <div className="consent">
                <label className="consent-row">
                  <input
                    type="checkbox"
                    name="isAdmin"
                    checked={formData.isAdmin}
                    onChange={handleChange}
                    disabled={!!adminInvite}
                  />
                  <span>I’m registering as an admin</span>
                </label>
              </div>
            )}

            {adminInvite && (
              <div className="form-group">
                <Label htmlFor="register-admin-company">Company</Label>
                <Input
                  id="register-admin-company"
                  className="form-input"
                  type="text"
                  value={adminInvite.companyName || ''}
                  placeholder="Company"
                  disabled
                  readOnly
                />
              </div>
            )}

            {(trialInvite || ownerReferralInvite) && (
              <div className="form-group">
                <Label htmlFor="register-trial-company">Company / workspace name</Label>
                <Input
                  id="register-trial-company"
                  className="form-input"
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="e.g. Acme Sales"
                  required
                  minLength={1}
                />
                <div className="form-help">
                  This becomes your Lanura workspace. You can rename it later in Settings.
                </div>
              </div>
            )}
          </>
        )}

        <Rise as="div" index={7} className="consent-group">
          <div className="consent">
            <label className="consent-row">
              <input type="checkbox" />
              <span>
                I want to receive updates, special offers and promotional emails. I understand that
                I can opt out at any time.
              </span>
            </label>
          </div>
          <div className={`consent${termsTouched && !termsAccepted ? ' consent--invalid' : ''}`}>
            <label className="consent-row">
              <input
                type="checkbox"
                checked={termsAccepted}
                aria-invalid={termsTouched && !termsAccepted}
                aria-describedby={termsTouched && !termsAccepted ? 'terms-required' : undefined}
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked) setError('');
                }}
              />
              <span>
                I agree to the <a href="/terms">Terms of Service</a> and acknowledge Lanura's{' '}
                <a href="/privacy">Privacy Policy</a>
              </span>
            </label>
            <AnimatePresence initial={false}>
              {termsTouched && !termsAccepted && (
                <motion.div
                  key="terms-required"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={SPRING_FAST}
                  style={{ overflow: 'hidden' }}
                >
                  <div id="terms-required" className="consent-error">
                    You need to accept the Terms of Service before signing up.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Rise>

        <Rise as="div" index={8}>
          <Button
            type="submit"
            variant="default"
            size="lg"
            className="register-btn"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </Button>
        </Rise>
      </form>

      <Rise as="div" index={9} className="have-account">
        Already have an account? <Link to="/login">Log in</Link>
      </Rise>
    </AuthShell>
  );
};

export default Register;
